import { Location } from 'history';
import { useInjection } from 'inversify-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ICurrentWorkspaceStore } from '../../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError } from '../../../models/error.model';
import { Home } from '../../../routes/config/Home';
import { Plan } from '../../user/user.store';
import { DatasetDetailsActions } from '../components/DatasetDetailsActions';
import { DatasetDetailsInformation } from '../components/DatasetDetailsInformation';
import { DeleteDatasetConfirmationModal } from '../components/DeleteDatasetConfirmationModal';
import { PublishDatasetConfirmationModal } from '../components/PublishDatasetConfirmationModal';
import { PublishDatasetNavigateAwayConfirmationModal } from '../components/PublishDatasetNavigateAwayConfirmationModal';
import { DatasetDetails } from '../DatasetDetails.context';
import { Datasets } from '../datasets.context';
import { useDatasetPermissions } from '../datasets.permissions';
import { IImagesUploaderService } from '../imagesUpload.service';

export const DatasetDetailsOverviewTabContainer = observer((): ReactElement => {
  const imagesUploaderService = useInjection(IImagesUploaderService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const datasetsPermissions = useDatasetPermissions();

  const { isLoading, withLoaderAsync } = useLoader();
  const detailsContext = useContext(DatasetDetails);
  const datasetsContext = useContext(Datasets);

  const { datasetId } = useParams<{ datasetId: string }>();
  const history = useHistory();

  const [showDatasetDeleteConfirmationModal, setShowDatasetDeleteConfirmationModal] = useState(false);
  const [showPublishDatasetConfirmationModal, setShowPublishDatasetConfirmationModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [navigationLocation, setNavigationLocation] = useState<Location | null>(null);

  const details = detailsContext.store.details;

  const handleDeleteDatasetAsync = async (): Promise<void> => {
    datasetsContext.service.markStale();
    await withLoaderAsync(async () => {
      await Promise.allSettled([detailsContext.service.deleteDatasetAsync(datasetId), imagesUploaderService.cancelUpload(datasetId)]);
      await datasetsContext.service.loadUserDatasetAsync();
    }, 'delete');

    history.push(Home.Datasets.List.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id }));
  };

  const handlePublishAsync = async (): Promise<void> => {
    datasetsContext.service.markStale();
    const result = await withLoaderAsync(async () => await detailsContext.service.publishDatasetAsync(datasetId), 'publish');

    if (result instanceof ACDLError && result.isBadRequestWithCode(['CAN_NOT_PUBLISH_LIMIT_EXCEEDED'])) {
      const errorContext = result.apiErrorResponse?.errorContext as { availableImageCount?: number; ownerPlan?: string };
      const availableImageCount = errorContext.availableImageCount || 0;
      const ownerPlan = (errorContext.ownerPlan as Plan) || Plan.Free;
      runInAction(() => {
        detailsContext.store.isImageCountExceeded = true;
        detailsContext.store.availableImageCount = availableImageCount;
        detailsContext.store.ownerPlan = ownerPlan;
      });
    }

    setShowPublishDatasetConfirmationModal(false);

    datasetsContext.store.refreshFromDetails(detailsContext.store.details);
  };

  const toggleDatasetDeleteConfirmationModal = (): void => {
    setShowDatasetDeleteConfirmationModal((prevState) => !prevState);
  };

  const togglePublishDatasetConfirmationModal = (): void => {
    setShowPublishDatasetConfirmationModal((prevState) => !prevState);
  };

  const handleDownload = async (): Promise<void> => {
    await detailsContext.service.downloadDatasetAsync(datasetId);
  };

  const handleBlockedNavigation = (location: Location): boolean => {
    if (location.pathname.includes('/datasets/') && location.pathname.includes('/preview/') && location.pathname.includes(details.id)) {
      return true;
    }
    setNavigationLocation(location);
    setShowModal(true);
    return false;
  };

  const onLeaveConfirm = async (): Promise<void> => {
    await detailsContext.service.publishDatasetAsync(details.id);
    if (navigationLocation) {
      history.push(navigationLocation);
    }
    setShowModal(false);
  };

  const handleKeepDraft = (): void => {
    setShowModal(false);
    if (navigationLocation) {
      history.push(navigationLocation);
    }
  };

  const handleCancel = (): void => {
    setShowModal(false);
    setNavigationLocation(null);
  };

  const { isImageCountExceeded, availableImageCount, ownerPlan } = detailsContext.store;

  return (
    <>
      <PublishDatasetNavigateAwayConfirmationModal
        intercept={details.status === 'Draft' && !showModal && datasetsPermissions.canPublishDataset().allowed && !showDatasetDeleteConfirmationModal}
        onBlockedNavigation={handleBlockedNavigation}
        showModal={showModal}
        onConfirm={onLeaveConfirm}
        onKeepDraft={handleKeepDraft}
        onCancel={handleCancel}
      />
      <DatasetDetailsInformation
        details={details}
        isLoading={isLoading}
      />
      <div>
        <DatasetDetailsActions
          isImageCountExceeded={isImageCountExceeded}
          availableImageCount={availableImageCount}
          ownerPlan={ownerPlan}
          details={details}
          isDatasetPublishable={details.status === 'Draft' && details.imagesCount > 0 && !isImageCountExceeded}
          isDeleteDisabled={details.projectsCount > 0}
          onDownload={handleDownload}
          onShowDatasetDeleteConfirmationModal={toggleDatasetDeleteConfirmationModal}
          onShowPublishDatasetConfirmationModal={togglePublishDatasetConfirmationModal}
        />
        <DeleteDatasetConfirmationModal
          onDeleteDatasetCancel={toggleDatasetDeleteConfirmationModal}
          onDatasetDeleteConfirm={handleDeleteDatasetAsync}
          datasetDeleteConfirmationText={details.name}
          showDatasetDeleteConfirmationModal={showDatasetDeleteConfirmationModal}
        />
        <PublishDatasetConfirmationModal
          onPublishDatasetCancel={togglePublishDatasetConfirmationModal}
          showPublishDatasetConfirmationModal={showPublishDatasetConfirmationModal}
          onDatasetPublishConfirm={handlePublishAsync}
        />
      </div>
    </>
  );
});
